import React, { useState, useCallback, useRef, useEffect } from 'react';
import { View, TextInput, Pressable, Modal, StyleSheet, ScrollView, Platform } from 'react-native';
import { Portal } from '@gorhom/portal';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { SELECT_PICKER_PORTAL_HOST } from '@/components/ui/SelectField';
import { useInlineSelectPicker } from '@/components/ui/SelectFieldModalContext';
import { useIosOverlayPresent } from '@/utils/useIosOverlayPresent';
import { ThemedText, Caption } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';

type TimePickerInputProps = {
  label?: string;
  value: string; // 24-hour format "HH:MM" or empty
  onValueChange: (value: string) => void;
  placeholder?: string;
  errorText?: string;
  helperText?: string;
  allowClear?: boolean;
  disabled?: boolean;
};

/**
 * Parse various time input formats to 24-hour "HH:MM" format
 * Supports: "7:03am", "7:03 AM", "19:03", "7:03 pm", "703am", "703", etc.
 */
const parseTimeInput = (input: string): string | null => {
  if (!input || !input.trim()) return null;
  
  const cleaned = input.trim().toLowerCase().replace(/\s+/g, '');
  
  // Check for AM/PM
  const isPM = cleaned.includes('pm') || cleaned.includes('p');
  const isAM = cleaned.includes('am') || cleaned.includes('a');
  const timeOnly = cleaned.replace(/[apm]/g, '').trim();
  
  let hours = 0;
  let minutes = 0;
  
  // Try to parse "HH:MM" or "H:MM" format
  if (timeOnly.includes(':')) {
    const parts = timeOnly.split(':');
    hours = parseInt(parts[0], 10);
    minutes = parseInt(parts[1], 10) || 0;
  } 
  // Try to parse "HHMM" or "HMM" or "HH" or "H" format
  else if (timeOnly.length >= 1) {
    const numValue = parseInt(timeOnly, 10);
    if (timeOnly.length <= 2) {
      // Just hours (e.g., "7" or "19")
      hours = numValue;
      minutes = 0;
    } else if (timeOnly.length === 3) {
      // "HMM" format (e.g., "703" -> 7:03)
      hours = Math.floor(numValue / 100);
      minutes = numValue % 100;
    } else if (timeOnly.length >= 4) {
      // "HHMM" format (e.g., "0703" -> 07:03)
      hours = Math.floor(numValue / 100);
      minutes = numValue % 100;
    }
  }
  
  // Validate parsed values
  if (isNaN(hours) || isNaN(minutes)) return null;
  if (minutes < 0 || minutes > 59) return null;
  
  // Convert 12-hour to 24-hour if AM/PM specified
  if (isPM && !isAM) {
    if (hours < 12) hours += 12;
  } else if (isAM && !isPM) {
    if (hours === 12) hours = 0;
  }
  
  // Validate hours
  if (hours < 0 || hours > 23) return null;
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

/**
 * Format 24-hour time to 12-hour display with AM/PM
 */
const formatTimeDisplay = (time24: string): string => {
  if (!time24 || !time24.includes(':')) return '';
  const [hourStr, minuteStr] = time24.split(':');
  const hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);
  
  if (isNaN(hour) || isNaN(minute)) return '';
  
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
};

/**
 * Pick a readable text color (black/white) for a given hex-ish background.
 * Falls back to scheme-based heuristics if parsing fails.
 */
const getReadableTextColor = (bgColor: string | undefined, fallback: '#000' | '#fff'): '#000' | '#fff' => {
  if (!bgColor) return fallback;
  const hex = bgColor.trim().replace(/^#/, '');
  const isShort = hex.length === 3;
  const isLong = hex.length === 6;
  if (!isShort && !isLong) return fallback;
  const full = isShort
    ? `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
    : hex;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if (![r, g, b].every((v) => Number.isFinite(v))) return fallback;
  // Relative luminance (rough). Higher => lighter background => choose black text.
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance > 0.6 ? '#000' : '#fff';
};

/**
 * TimePickerInput - A flexible time input component
 * 
 * Allows users to:
 * - Type any time (e.g., "7:03am", "1159pm", "19:30")
 * - Use a visual time picker modal
 * - Clear the selection
 */
export function TimePickerInput({
  label,
  value,
  onValueChange,
  placeholder = 'Enter time (e.g., 7:30 AM)',
  errorText,
  helperText,
  allowClear = true,
  disabled = false,
}: TimePickerInputProps) {
  const theme = useTheme();
  const useInlinePicker = useInlineSelectPicker();
  // If primary is very light (common in dark theme), black text is needed for contrast.
  const selectedPeriodTextColor = getReadableTextColor(
    theme.colors.primary,
    theme.colorScheme === 'dark' ? '#000' : '#fff'
  );
  const [pickerRequested, setPickerRequested] = useState(false);
  const showPicker = useIosOverlayPresent(pickerRequested);
  const [inputText, setInputText] = useState(value ? formatTimeDisplay(value) : '');
  const [pickerHour, setPickerHour] = useState(9);
  const [pickerMinute, setPickerMinute] = useState(0);
  const [pickerPeriod, setPickerPeriod] = useState<'AM' | 'PM'>('AM');
  const inputRef = useRef<TextInput>(null);

  const openTimePicker = useCallback(() => {
    if (disabled) return;
    setPickerRequested(true);
  }, [disabled]);
  
  // Sync display text when value changes externally
  useEffect(() => {
    if (value) {
      setInputText(formatTimeDisplay(value));
    } else {
      setInputText('');
    }
  }, [value]);
  
  // Initialize picker values from current value
  useEffect(() => {
    if (value && value.includes(':')) {
      const [hourStr, minuteStr] = value.split(':');
      const hour24 = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);
      
      if (!isNaN(hour24) && !isNaN(minute)) {
        setPickerPeriod(hour24 >= 12 ? 'PM' : 'AM');
        setPickerHour(hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24);
        setPickerMinute(minute);
      }
    }
  }, [value, showPicker]);
  
  const handleTextChange = useCallback((text: string) => {
    setInputText(text);
  }, []);
  
  const handleTextBlur = useCallback(() => {
    if (!inputText.trim()) {
      onValueChange('');
      return;
    }
    
    const parsed = parseTimeInput(inputText);
    if (parsed) {
      onValueChange(parsed);
      setInputText(formatTimeDisplay(parsed));
    } else {
      // Invalid input - revert to previous value
      setInputText(value ? formatTimeDisplay(value) : '');
    }
  }, [inputText, value, onValueChange]);
  
  const handleClear = useCallback(() => {
    setInputText('');
    onValueChange('');
  }, [onValueChange]);
  
  const handlePickerConfirm = useCallback(() => {
    let hour24 = pickerHour;
    if (pickerPeriod === 'PM' && pickerHour < 12) {
      hour24 = pickerHour + 12;
    } else if (pickerPeriod === 'AM' && pickerHour === 12) {
      hour24 = 0;
    }
    
    const timeValue = `${hour24.toString().padStart(2, '0')}:${pickerMinute.toString().padStart(2, '0')}`;
    onValueChange(timeValue);
    setPickerRequested(false);
  }, [pickerHour, pickerMinute, pickerPeriod, onValueChange]);
  
  const hasError = !!errorText;

  const closePicker = useCallback(() => setPickerRequested(false), []);

  const pickerContent = (
    <>
      <View style={styles.pickerHeader}>
        <ThemedText variant="h3">Select Time</ThemedText>
        <Pressable onPress={closePicker} hitSlop={8}>
          <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
        </Pressable>
      </View>

      <View style={styles.wheelsContainer}>
        <View style={styles.wheelColumn}>
          <Caption style={{ textAlign: 'center', marginBottom: 8 }}>Hour</Caption>
          <ScrollView
            style={[styles.wheelScroll, { borderColor: theme.colors.borderMuted }]}
            showsVerticalScrollIndicator={false}
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
          >
            {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((h) => (
              <Pressable
                key={h}
                onPress={() => setPickerHour(h)}
                style={[
                  styles.wheelItem,
                  pickerHour === h && { backgroundColor: theme.colors.primary + '20' },
                ]}
              >
                <ThemedText
                  variant={pickerHour === h ? 'bodyStrong' : 'body'}
                  style={{
                    textAlign: 'center',
                    color: pickerHour === h ? theme.colors.primary : theme.colors.text,
                  }}
                >
                  {h}
                </ThemedText>
              </Pressable>
            ))}
          </ScrollView>
        </View>

        <View style={styles.wheelColumn}>
          <Caption style={{ textAlign: 'center', marginBottom: 8 }}>Min</Caption>
          <ScrollView
            style={[styles.wheelScroll, { borderColor: theme.colors.borderMuted }]}
            showsVerticalScrollIndicator={false}
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
          >
            {Array.from({ length: 60 }, (_, i) => i).map((m) => (
              <Pressable
                key={m}
                onPress={() => setPickerMinute(m)}
                style={[
                  styles.wheelItem,
                  pickerMinute === m && { backgroundColor: theme.colors.primary + '20' },
                ]}
              >
                <ThemedText
                  variant={pickerMinute === m ? 'bodyStrong' : 'body'}
                  style={{
                    textAlign: 'center',
                    color: pickerMinute === m ? theme.colors.primary : theme.colors.text,
                  }}
                >
                  {m.toString().padStart(2, '0')}
                </ThemedText>
              </Pressable>
            ))}
          </ScrollView>
        </View>

        <View style={styles.wheelColumn}>
          <Caption style={{ textAlign: 'center', marginBottom: 8 }}> </Caption>
          <View style={{ gap: 8, paddingTop: 8 }}>
            {(['AM', 'PM'] as const).map((p) => (
              <Pressable
                key={p}
                onPress={() => setPickerPeriod(p)}
                style={[
                  styles.periodButton,
                  {
                    backgroundColor:
                      pickerPeriod === p ? theme.colors.primary : theme.colors.backgroundSecondary,
                    borderColor:
                      pickerPeriod === p ? theme.colors.primary : theme.colors.borderMuted,
                  },
                ]}
              >
                <ThemedText
                  variant="bodyStrong"
                  style={{
                    color: pickerPeriod === p ? selectedPeriodTextColor : theme.colors.text,
                    textAlign: 'center',
                  }}
                >
                  {p}
                </ThemedText>
              </Pressable>
            ))}
          </View>
        </View>
      </View>

      <View style={[styles.previewContainer, { backgroundColor: theme.colors.background }]}>
        <ThemedText variant="h2" style={{ textAlign: 'center' }}>
          {pickerHour}:{pickerMinute.toString().padStart(2, '0')} {pickerPeriod}
        </ThemedText>
      </View>

      <View style={styles.actionsContainer}>
        <Button label="Cancel" variant="ghost" onPress={closePicker} style={{ flex: 1 }} />
        <Button
          label="Confirm"
          variant="primary"
          onPress={handlePickerConfirm}
          style={{ flex: 1 }}
        />
      </View>
    </>
  );

  const pickerModal = (
    <Modal
      visible={showPicker}
      transparent
      animationType="fade"
      onRequestClose={closePicker}
      presentationStyle="overFullScreen"
    >
      <Pressable style={styles.modalOverlay} onPress={closePicker}>
        <Pressable
          style={[styles.pickerContainer, { backgroundColor: theme.colors.surface }]}
          onPress={(e) => e.stopPropagation()}
        >
          {pickerContent}
        </Pressable>
      </Pressable>
    </Modal>
  );
  
  return (
    <View style={{ gap: theme.spacing(0.5) }}>
      {label && (
        <ThemedText variant="bodyStrong" style={{ marginBottom: theme.spacing(0.5) }}>
          {label}
        </ThemedText>
      )}
      
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: theme.colors.surface,
          borderRadius: theme.radii.md,
          borderWidth: 1,
          borderColor: hasError ? theme.colors.error : theme.colors.borderMuted,
          paddingHorizontal: theme.spacing(1.5),
          minHeight: 48,
        }}
      >
        <TextInput
          ref={inputRef}
          value={inputText}
          onChangeText={handleTextChange}
          onBlur={handleTextBlur}
          placeholder={placeholder}
          placeholderTextColor={theme.colors.textSecondary}
          editable={!disabled}
          style={{
            flex: 1,
            fontSize: 16,
            color: disabled ? theme.colors.textSecondary : theme.colors.text,
            paddingVertical: theme.spacing(1.25),
          }}
          keyboardType="default"
          autoCapitalize="none"
          autoCorrect={false}
        />
        
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
          {allowClear && value && !disabled && (
            <Pressable
              onPress={handleClear}
              hitSlop={8}
              style={({ pressed }) => ({ opacity: pressed ? 0.6 : 1 })}
            >
              <Ionicons name="close-circle" size={20} color={theme.colors.textSecondary} />
            </Pressable>
          )}
          
          <Pressable
            onPress={openTimePicker}
            hitSlop={8}
            style={({ pressed }) => ({ 
              opacity: pressed ? 0.6 : 1,
              padding: theme.spacing(0.5),
            })}
            disabled={disabled}
          >
            <Ionicons 
              name="time-outline" 
              size={22} 
              color={disabled ? theme.colors.borderMuted : theme.colors.primary} 
            />
          </Pressable>
        </View>
      </View>
      
      {(errorText || helperText) && (
        <Caption 
          color={hasError ? theme.colors.error : theme.colors.textSecondary}
          style={{ marginTop: theme.spacing(0.25) }}
        >
          {errorText || helperText}
        </Caption>
      )}

      {useInlinePicker && showPicker && (
        <View
          style={{
            marginTop: theme.spacing(1),
            borderWidth: 1,
            borderColor: theme.colors.borderMuted,
            borderRadius: theme.radii.lg,
            padding: theme.spacing(1.5),
            gap: theme.spacing(1.5),
            backgroundColor: theme.colors.surface,
          }}
        >
          {pickerContent}
        </View>
      )}
      
      {!useInlinePicker &&
        (Platform.OS === 'ios' ? (
          <Portal hostName={SELECT_PICKER_PORTAL_HOST}>{pickerModal}</Portal>
        ) : (
          pickerModal
        ))}
    </View>
  );
}

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  pickerContainer: {
    width: '100%',
    maxWidth: 360,
    borderRadius: 16,
    padding: 20,
    gap: 16,
  },
  pickerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  wheelsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 12,
  },
  wheelColumn: {
    alignItems: 'center',
  },
  wheelScroll: {
    height: 180,
    width: 70,
    borderRadius: 12,
    borderWidth: 1,
    overflow: 'hidden',
  },
  wheelItem: {
    paddingVertical: 10,
    paddingHorizontal: 8,
  },
  periodButton: {
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: 12,
    borderWidth: 1,
  },
  previewContainer: {
    paddingVertical: 16,
    borderRadius: 12,
  },
  actionsContainer: {
    flexDirection: 'row',
    gap: 12,
  },
});

export default TimePickerInput;

